// Phase N9B: JS wrapper for the native, process-wide (app-wide, not
// single-Activity) foreground signal. This is what lets PresenceGate stop
// trusting document.visibilitychange on Android - that event fires
// whenever ANY Activity occludes MainActivity's WebView, including a
// same-app native screen (SnapNativeCameraActivity,
// LivoraNativeCallRoomActivity), which is not the same thing as the user
// actually leaving the app.

function getNativeAppForegroundPlugin() {
  try {
    if (typeof window === "undefined") {
      return null;
    }
    return window.Capacitor?.Plugins?.NativeAppForeground ?? null;
  } catch {
    return null;
  }
}

/**
 * Synchronous-ish current-state read (present=false on web/unsupported
 * platforms, or if the plugin genuinely isn't there) - callers should seed
 * their own state from this once, then rely on the listener for changes.
 */
export async function getNativeAppForegroundStatus() {
  const plugin = getNativeAppForegroundPlugin();
  if (!plugin || typeof plugin.getState !== "function") {
    return { present: false, isForeground: true, reason: "plugin_not_present" };
  }

  try {
    const result = await plugin.getState();
    return {
      present: result?.present === true,
      isForeground: result?.isForeground !== false,
    };
  } catch (error) {
    return {
      present: true,
      isForeground: true,
      reason: "get_state_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Subscribe to the native "foregroundChanged" event. Returns a handle with
 * .remove() to unsubscribe; if the plugin isn't present, returns a no-op
 * handle so callers never need to branch.
 *
 * @param {(isForeground: boolean) => void} callback
 */
export function addNativeAppForegroundChangeListener(callback) {
  const plugin = getNativeAppForegroundPlugin();
  if (!plugin || typeof plugin.addListener !== "function") {
    return { remove: async () => {} };
  }

  return plugin.addListener("foregroundChanged", (data) => {
    callback(data?.isForeground === true);
  });
}
